import { useCallback, useRef } from "react";

export function RangeSlider({
  min,
  max,
  low,
  high,
  step = 1,
  onChange,
  ariaLabel = "Range",
}: {
  min: number;
  max: number;
  low: number;
  high: number;
  step?: number;
  onChange: (low: number, high: number) => void;
  ariaLabel?: string;
}) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<"low" | "high" | null>(null);

  const valueFromClientX = useCallback((clientX: number) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect?.width) return min;
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const raw = min + ratio * (max - min);
    const stepped = step <= 1 ? Math.round(raw) : Math.round(raw / step) * step;
    return Math.max(min, Math.min(max, stepped));
  }, [max, min, step]);

  const percent = (value: number) => ((value - min) / (max - min)) * 100;

  const startDrag = (thumb: "low" | "high", event: React.PointerEvent<HTMLButtonElement>) => {
    dragRef.current = thumb;
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const moveDrag = (event: React.PointerEvent<HTMLButtonElement>) => {
    const thumb = dragRef.current;
    if (!thumb) return;

    const next = valueFromClientX(event.clientX);
    if (thumb === "low") {
      onChange(Math.min(next, high), high);
    } else {
      onChange(low, Math.max(next, low));
    }
  };

  const endDrag = () => {
    dragRef.current = null;
  };

  const onTrackPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect) return;

    const clickValue = valueFromClientX(event.clientX);
    const distLow = Math.abs(clickValue - low);
    const distHigh = Math.abs(clickValue - high);
    if (distLow <= distHigh) {
      onChange(Math.min(clickValue, high), high);
    } else {
      onChange(low, Math.max(clickValue, low));
    }
  };

  return (
    <div className="range-slider" aria-label={ariaLabel}>
      <div
        className="range-slider-track"
        ref={trackRef}
        onPointerDown={onTrackPointerDown}
      >
        <div className="range-slider-rail" aria-hidden="true" />
        <div
          className="range-slider-fill"
          style={{
            left: `${percent(low)}%`,
            width: `${Math.max(0, percent(high) - percent(low))}%`,
          }}
          aria-hidden="true"
        />
        <button
          className="range-slider-thumb range-slider-thumb--low"
          type="button"
          style={{ left: `${percent(low)}%` }}
          aria-label={`Minimum ${low}`}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={low}
          onPointerDown={(event) => startDrag("low", event)}
          onPointerMove={moveDrag}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        />
        <button
          className="range-slider-thumb range-slider-thumb--high"
          type="button"
          style={{ left: `${percent(high)}%` }}
          aria-label={`Maximum ${high}`}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={high}
          onPointerDown={(event) => startDrag("high", event)}
          onPointerMove={moveDrag}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        />
      </div>
    </div>
  );
}
